import { cur } from '@/lib/currency';
import { useEffect, useState } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { DateFilter } from '@/components/DateFilter';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DollarSign, FileText, Wrench } from 'lucide-react';
import api from '@/lib/api';
import { useBranchStore } from '@/stores';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { ExportButtons } from '@/components/ExportButtons';
import { BranchFilter } from '@/components/BranchFilter';
import { useLanguage } from '@/contexts/LanguageContext';
import { toLocalYmd } from '@/lib/dateRange';

export default function CreditReportPage() {
  const { t } = useLanguage();
  const [reportData, setReportData] = useState(null);
  const [dateFilter, setDateFilter] = useState({ start: null, end: null, period: "all" });
  const { branches, fetchBranches: _fetchBr } = useBranchStore();
  const [branchFilter, setBranchFilter] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showReceiveDialog, setShowReceiveDialog] = useState(false);
  const [receivingSale, setReceivingSale] = useState(null);
  const [receivePayment, setReceivePayment] = useState({ payment_mode: 'cash', amount: '', discount: '' });

  useEffect(() => {
    fetchReport();
  }, [dateFilter?.start, dateFilter?.end]);

  const fetchReport = async () => {
    try {
      setLoading(true);
      const params = {};
      if (dateFilter?.start) params.start_date = typeof dateFilter.start === 'string' ? dateFilter.start.slice(0,10) : toLocalYmd(dateFilter.start);
      if (dateFilter?.end) params.end_date = typeof dateFilter.end === 'string' ? dateFilter.end.slice(0,10) : toLocalYmd(dateFilter.end);
      const res = await api.get('/reports/credit-sales', { params });
      setReportData(res.data);
    } catch (error) {
      console.error('Credit report error:', error);
      toast.error('Failed to load credit report');
    } finally {
      setLoading(false);
    }
  };

  const getBranchNames = () => branchFilter.map(bid => branches.find(b => b.id === bid)?.name).filter(Boolean);

  const handleReceiveCredit = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/sales/${receivingSale.id}/receive-credit`, {
        payment_mode: receivePayment.payment_mode,
        amount: parseFloat(receivePayment.amount) || 0,
        discount: parseFloat(receivePayment.discount) || 0
      });
      toast.success('Credit payment received');
      setShowReceiveDialog(false);
      setReceivePayment({ payment_mode: 'cash', amount: '', discount: '' });
      fetchReport();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to receive payment');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">Loading...</div>
      </DashboardLayout>
    );
  }

  const getStatusBadge = (status) => {
    switch (status) {
      case 'paid':
        return <Badge className="bg-success/20 text-success border-success/30">Paid</Badge>;
      case 'partial':
        return <Badge className="bg-warning/20 text-warning border-warning/30">Partial</Badge>;
      default:
        return <Badge className="bg-error/20 text-error border-error/30">Pending</Badge>;
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-start flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-4">

              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-orange-500 to-amber-600 flex items-center justify-center shadow-lg shadow-orange-200/50">

                <FileText size={24} className="text-white" />

              </div>

              <div>

                <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground" data-testid="credit-report-title">Credit Sales Report</h1>

                <p className="text-sm text-muted-foreground mt-0.5">Credit sales tracking and reports</p>

              </div>

            </div>
            <p className="text-muted-foreground">Track credit sales, received payments, and outstanding balances</p>
          </div>
          <div className="flex gap-3 items-center">
            <BranchFilter onChange={setBranchFilter} />
            <DateFilter onFilterChange={setDateFilter} />
            <ExportButtons branchIds={branchFilter} dataType="credit_sales" startDate={dateFilter?.start} endDate={dateFilter?.end} />
            <Button
              variant="outline"
              size="sm"
              data-testid="fix-credit-sales-btn-inline"
              onClick={async () => {
                if (!window.confirm('Rebuild credit_amount on every sale from payment_details?\n\nThis fixes legacy rows where online-only or other non-cash/bank payments were mis-stored as credit sales. Safe — no data is deleted.')) return;
                try {
                  const r = await api.post('/sales/recalc-credit');
                  toast.success(`Fixed ${r.data.updated} of ${r.data.total_sales} sales. Refreshing…`);
                  fetchReport();
                } catch (e) {
                  toast.error(e.response?.data?.detail || 'Recalc failed');
                }
              }}
              className="gap-1.5 border-warning/40 text-warning hover:bg-warning/10"
              title="Fix legacy sales where online-only payments were incorrectly tagged as credit"
            >
              <Wrench size={14} /> Fix Credit Totals
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="border-border bg-gradient-to-br from-credit/10 to-credit/5">
            <CardHeader>
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Credit Given</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold font-outfit text-credit" data-testid="total-credit-given">
                {cur()} {Number(reportData?.summary?.total_credit_given || 0).toFixed(2)}
              </div>
            </CardContent>
          </Card>

          <Card className="border-border bg-gradient-to-br from-success/10 to-success/5">
            <CardHeader>
              <CardTitle className="text-sm font-medium text-muted-foreground">Amount Received</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold font-outfit text-success" data-testid="total-credit-received">
                {cur()} {Number(reportData?.summary?.total_credit_received || 0).toFixed(2)}
              </div>
            </CardContent>
          </Card>

          <Card className="border-border bg-gradient-to-br from-error/10 to-error/5">
            <CardHeader>
              <CardTitle className="text-sm font-medium text-muted-foreground">Remaining Balance</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold font-outfit text-error" data-testid="total-credit-remaining">
                {cur()} {Number(reportData?.summary?.total_credit_remaining || 0).toFixed(2)}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Detailed Credit Sales Table */}
        <Card className="border-border">
          <CardHeader>
            <CardTitle className="font-outfit">Credit Sales Details</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="credit-sales-table">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left p-3 font-medium text-sm">Date</th>
                    <th className="text-left p-3 font-medium text-sm">Type</th>
                    <th className="text-left p-3 font-medium text-sm">Branch</th>
                    <th className="text-left p-3 font-medium text-sm">Customer</th>
                    <th className="text-right p-3 font-medium text-sm">Total</th>
                    <th className="text-right p-3 font-medium text-sm">Discount</th>
                    <th className="text-right p-3 font-medium text-sm">Final</th>
                    <th className="text-right p-3 font-medium text-sm">Credit Given</th>
                    <th className="text-right p-3 font-medium text-sm">Received</th>
                    <th className="text-right p-3 font-medium text-sm">Remaining</th>
                    <th className="text-center p-3 font-medium text-sm">Status</th>
                    <th className="text-right p-3 font-medium text-sm">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData?.credit_sales?.filter(s => branchFilter.length === 0 || getBranchNames().includes(s.branch)).map((sale) => (
                    <tr key={sale.id} className="border-b border-border hover:bg-secondary/50" data-testid="credit-sale-row">
                      <td className="p-3 text-sm">{format(new Date(sale.date), 'MMM dd, yyyy')}</td>
                      <td className="p-3 text-sm capitalize">{sale.sale_type}</td>
                      <td className="p-3 text-sm">{sale.branch}</td>
                      <td className="p-3 text-sm">{sale.reference || '-'}</td>
                      <td className="p-3 text-sm text-right font-medium"> {cur()} {sale.total_amount.toFixed(2)}</td>
                      <td className="p-3 text-sm text-right text-error">{(sale.discount || 0) > 0 ? `-${cur()} ${sale.discount.toFixed(2)}` : '-'}</td>
                      <td className="p-3 text-sm text-right font-medium text-primary"> {cur()} {(sale.final_amount || sale.total_amount).toFixed(2)}</td>
                      <td className="p-3 text-sm text-right text-credit font-bold"> {cur()} {sale.credit_given.toFixed(2)}</td>
                      <td className="p-3 text-sm text-right text-success"> {cur()} {sale.credit_received.toFixed(2)}</td>
                      <td className="p-3 text-sm text-right text-error font-bold"> {cur()} {sale.remaining.toFixed(2)}</td>
                      <td className="p-3 text-center">{getStatusBadge(sale.status)}</td>
                      <td className="p-3 text-right">
                        {sale.remaining > 0 && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => { setReceivingSale(sale); setShowReceiveDialog(true); }}
                            data-testid="receive-button"
                            className="h-8"
                          >
                            <DollarSign size={14} className="mr-1" />
                            Receive
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {(!reportData?.credit_sales || reportData.credit_sales.length === 0) && (
                    <tr>
                      <td colSpan={12} className="p-8 text-center text-muted-foreground">
                        No credit sales found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Receive Credit Dialog */}
        <Dialog open={showReceiveDialog} onOpenChange={setShowReceiveDialog}>
          <DialogContent data-testid="receive-credit-dialog">
            <DialogHeader>
              <DialogTitle className="font-outfit">Receive Credit Payment</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleReceiveCredit} className="space-y-4">
              <div>
                <p className="text-sm text-muted-foreground mb-2">
                  Sale Amount: <span className="font-medium text-foreground"> {cur()} {receivingSale?.total_amount?.toFixed(2)}</span>
                </p>
                <p className="text-sm text-muted-foreground">
                  Remaining Credit: <span className="font-bold text-credit"> {cur()} {receivingSale?.remaining?.toFixed(2)}</span>
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Payment Amount</Label>
                  <Input type="number" step="0.01" value={receivePayment.amount} data-testid="receive-amount-input"
                    onChange={(e) => setReceivePayment({ ...receivePayment, amount: e.target.value })} placeholder="0.00" />
                </div>
                <div>
                  <Label>Discount</Label>
                  <Input type="number" step="0.01" value={receivePayment.discount} data-testid="receive-discount-input"
                    onChange={(e) => setReceivePayment({ ...receivePayment, discount: e.target.value })} placeholder="0.00" />
                </div>
              </div>
              {(parseFloat(receivePayment.amount) > 0 || parseFloat(receivePayment.discount) > 0) && (
                <div className="p-3 bg-secondary/50 rounded-lg space-y-1 text-sm">
                  <div className="flex justify-between"><span>Payment:</span><span> {cur()} {(parseFloat(receivePayment.amount) || 0).toFixed(2)}</span></div>
                  {parseFloat(receivePayment.discount) > 0 && <div className="flex justify-between"><span>Discount:</span><span className="text-error">-SR {(parseFloat(receivePayment.discount) || 0).toFixed(2)}</span></div>}
                  <div className="flex justify-between border-t pt-1 font-bold"><span>Total Settled:</span><span className="text-success"> {cur()} {((parseFloat(receivePayment.amount) || 0) + (parseFloat(receivePayment.discount) || 0)).toFixed(2)}</span></div>
                </div>
              )}
              <div>
                <Label>Payment Mode</Label>
                <Select value={receivePayment.payment_mode} onValueChange={(val) => setReceivePayment({ ...receivePayment, payment_mode: val })}>
                  <SelectTrigger data-testid="receive-mode-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="cash">Cash</SelectItem><SelectItem value="bank">Bank</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="flex gap-3">
                <Button type="submit" data-testid="submit-receive-button" className="rounded-full">Receive Payment</Button>
                <Button type="button" variant="outline" onClick={() => setShowReceiveDialog(false)} className="rounded-full">Cancel</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
